'use client'

import AnimatedMainMenuButton from '../../../components/layout/header/AnimatedMainMenuButton'
import Logo from '../../../components/ui/logo'
import { CMSLink } from '../CMSLink'
import { Button } from '../ui/button'
import { addActiveScroll } from '@telesoft/lib/web/add-active-scroll'
import { cn } from '@telesoft/lib/web/cn'
import { useWindowScroll } from '@telesoft/lib/web/use-window-scroll'
import { MainMenu, MainMenuCtaButton } from '@telesoft/types/payload-types'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger, // navigationMenuTriggerStyle,
} from '@telesoft/ui/v2/components/ui/navigation-menu'
import Link from 'next/link'
import * as React from 'react'
import { IoMdReturnRight } from 'react-icons/io'
import { VscChevronRight } from 'react-icons/vsc'

export function HeaderMenu({
  variant,
  mainMenu,
  ctaButtons,
}: {
  variant: 'fixed' | 'default'
  mainMenu: MainMenu
  ctaButtons: MainMenuCtaButton
}) {
  const { menus } = mainMenu as MainMenu
  const filteredMenus = menus?.filter(m => m.label === 'Products' || m.label === 'Services')
  const isCtaButtons = ctaButtons?.buttons && ctaButtons?.buttons?.length > 0 && ctaButtons.buttons

  const siteHeaderRef = React.useRef(null)
  addActiveScroll(siteHeaderRef)

  const { y } = useWindowScroll()
  const isScrolling = y > 0 ? true : false

  return (
    <NavigationMenu variant={variant}>
      <div
        className={cn(
          `border-b border-transparent body-font py-4 transition duration-200 ease-in-out`,
          {
            'innerStickyAlt text-white': variant === 'fixed',
            'text-black': variant === 'fixed' && isScrolling,
            'innerSticky bg-white text-black': variant !== 'fixed',
            'border-b-black bg-white': isScrolling,
          },
        )}
      >
        <NavigationMenuList>
          <Logo inverted={variant === 'fixed' ? (isScrolling ? false : true) : false} />
          <div className="hidden xl:flex col-span-5 w-full justify-end ms-4 items-end">
            {filteredMenus?.map((menu) => {
              return (
                <NavigationMenuItem key={menu.id}>
                  <NavigationMenuTrigger
                    className={cn(
                      `inline-flex py-7 items-center text-sm xl:text-base px-3 xl:px-4 font-medium capitalize relative`,
                      {},
                    )}
                  >
                    {menu.label}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid gap-3 bg-black/90 md:w-[400px] py-4 lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
                      {menu.menuGroups?.map((menuGroup) => {
                        // const groupLink = menuGroup.link
                        const { links } = menuGroup
                        return (
                          <ul
                            key={menuGroup.id}
                            className={`col-span-8 place-self-start w-full space-y-2`}
                          >
                            <li className="font-terminal mb-1.5 block text-sm py-1.5 cursor-default uppercase text-white px-5 xl:px-8 2xl:px-10">
                              {menuGroup.label}
                            </li>

                            {links?.map((item) => {
                              const { link } = item
                              return (
                                <li key={item.id}>
                                  <NavigationMenuLink asChild>
                                    <CMSLink showLabel={false} {...link}>
                                      <div className="text-white w-full font-medium flex flex-col py-1.5 px-5 xl:px-8 2xl:px-10 border-b border-transparent hover:border-telesoft hover:bg-white hover:bg-opacity-10 transition-all duration-300">
                                        <div className="flex justify-start links-center">
                                          <IoMdReturnRight className="mr-3 mt-1" />
                                          {link.label}
                                        </div>

                                        {item.hasDescription && (
                                          <span className="ml-[29px] mt-1 font-light text-xs">
                                            {item.description}
                                          </span>
                                        )}
                                      </div>
                                    </CMSLink>
                                  </NavigationMenuLink>
                                </li>
                              )
                            })}
                          </ul>
                        )
                      })}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              )
            })}
          </div>
          <AnimatedMainMenuButton
            inverted={variant === 'fixed' ? (isScrolling ? false : true) : false}
            mainMenu={mainMenu}
          />
          {isCtaButtons && (
            <>
              <div className="col-span-2 hidden 2xl:flex justify-end items-center">
                <CMSLink
                  className={cn("flex-grow", {
                    "border-white text-white hover:border-black": variant === "fixed" && !isScrolling,
                  })}
                  {...isCtaButtons[0].link} />
              </div>
              {isCtaButtons[1] && (
                <div className="col-span-2 hidden md:flex md:col-span-3 md:col-start-5 md:col-end-8 lg:col-span-5 xl:col-span-4 2xl:col-span-2 justify-end items-center">
                  <CMSLink
                    className={cn("flex-grow", {
                      "text-white": variant === "fixed" && !isScrolling,
                    })}
                    {...isCtaButtons[1].link} />
                </div>
              )}
            </>
          )}
          {!isCtaButtons && (
            <>
              <div className="col-span-2 hidden 2xl:flex justify-end items-center">
                <Link passHref href="/contact-us" legacyBehavior>
                  <Button
                    id="ctaNav"
                    variant={variant === 'fixed' ? 'outline' : 'outline'}
                    className={`flex-grow ${variant === 'fixed'
                      ? isScrolling
                        ? 'border-black'
                        : 'border-white text-white hover:border-black'
                      : 'text-black'
                      }`}
                  >
                    Contact
                  </Button>
                </Link>
              </div>
              <div className="col-span-2 hidden md:flex md:col-span-3 md:col-start-5 md:col-end-8 lg:col-span-5 xl:col-span-4 2xl:col-span-2 justify-end items-center">
                <Link passHref href="/free-trial" legacyBehavior>
                  <Button
                    variant="default"
                    className={cn(`flex-grow`, {
                      'text-white': variant === 'fixed' && !isScrolling,
                    })}
                  >
                    <VscChevronRight className="mr-1" />
                    Free Trial
                  </Button>
                </Link>
              </div>
            </>
          )}
        </NavigationMenuList>
      </div >
    </NavigationMenu >
  )
}

const ListItem = React.forwardRef<React.ElementRef<'a'>, React.ComponentPropsWithoutRef<'a'>>(
  ({ className, title, children, ...props }, ref) => {
    return (
      <li>
        <NavigationMenuLink asChild>
          <a
            ref={ref}
            className={cn(
              'block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
              className,
            )}
            {...props}
          >
            <div className="text-sm font-medium leading-none">{title}</div>
            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">{children}</p>
          </a>
        </NavigationMenuLink>
      </li>
    )
  },
)
ListItem.displayName = 'ListItem'
